import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ImagePlaceholder } from '@/components/ImagePlaceholder';
import { HowItWorks } from '@/components/HowItWorks';
import { WhatsAppButton } from '@/components/WhatsAppButton';
import { Skeleton, SkeletonText } from '@/components/Skeleton';
import { findSection } from '@/lib/cms/section-types';
import { getCmsDocument, type CmsPageLocale } from '@/lib/db/cms-pages';
import { listAdvisors, type Advisor } from '@/lib/db/admin/advisors';
import { ABOUT_META } from '@/lib/seo/site';
import { useSeoHead } from '@/lib/seo/useSeoHead';

type Member = {
  name: string;
  role: string;
  email: string;
  photo_url?: string | null;
};
type Step = { number: string; title: string; body: string };

// Mirrors .team-grid__card (media + body) so the advisors grid keeps its shape
// while the advisors table loads, instead of popping in late.
function TeamCardSkeleton() {
  return (
    <article className="team-grid__card" aria-busy="true">
      <div className="team-grid__media">
        <Skeleton width="100%" height="100%" />
      </div>
      <div className="team-grid__body">
        <Skeleton width="40%" height={9} />
        <div style={{ height: 8 }} />
        <Skeleton width="70%" height={16} />
      </div>
    </article>
  );
}

export function AboutPage() {
  const { t, i18n } = useTranslation();
  useSeoHead(ABOUT_META);
  const steps = t('about.methodology.steps', { returnObjects: true }) as Step[];

  const [dbAdvisors, setDbAdvisors] = useState<Advisor[]>([]);
  const [advisorsLoading, setAdvisorsLoading] = useState(true);
  useEffect(() => {
    listAdvisors()
      .then((list) => setDbAdvisors(list.filter((a) => a.is_active)))
      .catch(() => {})
      .finally(() => setAdvisorsLoading(false));
  }, []);

  // Real advisors only — no fabricated fallback. When the table is empty the
  // team section is omitted entirely (see render) rather than exposing
  // placeholder identities + emails on this public, anon-readable page.
  const members: Member[] = dbAdvisors.map((a) => ({
    name: `${a.first_name} ${a.last_name}`,
    role: a.title,
    email: a.email ?? '',
    photo_url: a.photo_url,
  }));

  const locale = (
    ['en', 'fr', 'ar'].includes(i18n.language?.split('-')[0] ?? '')
      ? (i18n.language.split('-')[0] as CmsPageLocale)
      : 'en'
  ) as CmsPageLocale;

  const [cmsTitle, setCmsTitle] = useState<string | null>(null);
  const [cmsBody, setCmsBody] = useState<string | null>(null);
  const [cmsImageUrl, setCmsImageUrl] = useState<string | null>(null);
  const [cmsImageAlt, setCmsImageAlt] = useState<string | null>(null);
  const [cmsLoading, setCmsLoading] = useState(true);

  // CMS copy is strict: while loading, when the row/section is absent or
  // hidden, or when the admin emptied a field, the element is simply not
  // rendered — no i18n fallback resurrects content.
  useEffect(() => {
    setCmsLoading(true);
    getCmsDocument('about', locale)
      .then((doc) => {
        const rt = findSection(doc, 'rich_text');
        const img = findSection(doc, 'image');
        setCmsTitle(rt && rt.visible ? rt.title || null : null);
        setCmsBody(rt && rt.visible ? rt.html || null : null);
        setCmsImageUrl(img && img.visible && img.image_url ? img.image_url : null);
        setCmsImageAlt(img && img.visible ? img.alt || null : null);
      })
      .catch(() => {})
      .finally(() => setCmsLoading(false));
  }, [locale]);

  const title = cmsTitle;
  const bodyHtml = cmsBody ?? '';

  return (
    <div className="public">
      <section className="public__section--ink" style={{ paddingTop: 0, paddingBottom: 0 }}>
        <div className="about-hero">
          <div>
            <div className="about-hero__brand">DDM · Luxarabia</div>
            {cmsLoading ? (
              <>
                <Skeleton width="70%" height={44} style={{ margin: '8px 0 20px' }} />
                <SkeletonText lines={3} lastWidth="65%" lineHeight={14} />
              </>
            ) : (
              <>
                {title && <h1 className="about-hero__title">{title}</h1>}
                {bodyHtml && (
                  <div
                    className="about-hero__lead"
                    dangerouslySetInnerHTML={{ __html: bodyHtml }}
                  />
                )}
              </>
            )}
          </div>
          {cmsImageUrl && (
            <div className="about-hero__media">
              <img
                src={cmsImageUrl}
                alt={cmsImageAlt ?? ''}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: '0.75rem',
                }}
              />
            </div>
          )}
        </div>
      </section>

      {(advisorsLoading || members.length > 0) && (
        <section className="public__section public__section--white">
          <div className="public__inner">
            <div className="team-grid__head">
              <div className="public__kicker">{t('about.team.kicker')}</div>
              <h2 className="team-grid__title">{t('about.team.title')}</h2>
            </div>
            <div className="team-grid">
              {advisorsLoading
                ? Array.from({ length: 3 }, (_, i) => <TeamCardSkeleton key={i} />)
                : members.map((m, i) => (
                    <article key={i} className="team-grid__card">
                      <div className="team-grid__media">
                        {m.photo_url ? (
                          <>
                            <img
                              src={m.photo_url}
                              alt=""
                              aria-hidden="true"
                              className="team-grid__media-backdrop"
                            />
                            <img
                              src={m.photo_url}
                              alt={m.name}
                              className="team-grid__media-photo"
                            />
                          </>
                        ) : (
                          <ImagePlaceholder label={t('about.team.imageLabel')} />
                        )}
                      </div>
                      <div className="team-grid__body">
                        <div className="team-grid__role">{m.role}</div>
                        <div className="team-grid__name">{m.name}</div>
                        {m.email && <div className="team-grid__email">{m.email}</div>}
                      </div>
                    </article>
                  ))}
            </div>
          </div>
        </section>
      )}

      <section className="public__section public__section--paper">
        <div className="public__inner">
          <div className="how-it-works__head">
            <div className="public__kicker">{t('about.methodology.kicker')}</div>
            <h2 className="how-it-works__title">{t('about.methodology.title')}</h2>
            <p
              className="public__lead public__lead--paper"
              style={{ maxWidth: 560, margin: '16px auto 0' }}
            >
              {t('about.methodology.lead')}
            </p>
          </div>
          <HowItWorks steps={steps} variant="methodology" />
        </div>
      </section>

      <section className="public-cta-dark" style={{ padding: '64px 0' }}>
        <div className="public-cta-dark__inner">
          <h2 className="public-cta-dark__title" style={{ marginBottom: 32 }}>
            {t('about.cta.title')}
          </h2>
          <div className="public__cta-row public__cta-row--center">
            <Link to="/" className="btn btn--paper btn--lg">
              {t('actions.browseCatalogue')}
            </Link>
            <WhatsAppButton label={t('actions.contactWhatsapp')} size="lg" trackSource="about" />
          </div>
        </div>
      </section>
    </div>
  );
}
